import React, { useEffect, useState } from 'react';
import { Search, Bell, Trash2 } from 'lucide-react';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const formatDate = (d) => d ? new Date(d).toLocaleString('es-ES') : '—';

const STATUS_BADGE = {
  pending: 'badge-warning',
  sent: 'badge-success',
  completed: 'badge-success',
  canceled: 'badge-gray',
  failed: 'badge-danger',
};

const Reminders = () => {
  const modal = useModal();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUserPreference('reminders:search', '');
  const [statusFilter, setStatusFilter] = useUserPreference('reminders:status', '');
  const [viewMode, setViewMode] = useViewMode('reminders');
  const fl = useFieldLayout('reminders', viewMode);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/minerva-reminders?limit=200');
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      modal.error('Error cargando recordatorios');
    } finally {
      setLoading(false);
    }
  };

  const remove = (id) => {
    modal.confirm('¿Borrar este recordatorio?', async () => {
      try {
        await api.delete(`/minerva-reminders/${id}`);
        await load();
      } catch (e) {
        modal.error('Error borrando');
      }
    });
  };

  const filtered = items.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (r.title || '').toLowerCase().includes(q) ||
      (r.targetName || '').toLowerCase().includes(q) ||
      (r.targetPhone || '').toLowerCase().includes(q);
    const matchStatus = !statusFilter || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const columns = [
    { key: 'title', header: 'Título', className: 'dt-strong', fixed: true, render: (r) => (
      <div>
        {r.title}
        <div className="dt-sub" style={{ fontWeight: 400 }}>{formatDate(r.remindAt)}</div>
      </div>
    ) },
    { key: 'status', header: 'Estado', className: 'dt-nowrap', render: (r) => (
      <span className={`badge ${STATUS_BADGE[r.status] || 'badge-gray'}`}>{r.status}</span>
    ) },
    { key: 'targetName', header: 'Destinatario', render: (r) => r.targetName || '—' },
    { key: 'targetPhone', header: 'Tel', render: (r) => r.targetPhone || '—' },
    { key: 'notifyMethod', header: 'Método', render: (r) => r.notifyMethod },
    { key: 'attempts', header: 'Intentos', className: 'num', render: (r) => `${r.attempts}/${r.maxAttempts}` },
    { key: 'description', header: 'Detalle', render: (r) => r.description || '—' },
    { key: 'actions', header: '', className: 'col-actions', render: (r) => (
      <div className="dt-actions">
        <button className="btn-icon" onClick={() => remove(r.id)} title="Borrar" style={{ color: '#ef4444' }}><Trash2 size={16} /></button>
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Recordatorios</h1>
          <p className="page-subtitle">{items.length} recordatorios registrados</p>
        </div>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input className="form-input" placeholder="Buscar por título, destinatario, teléfono..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <select className="form-input" style={{ width: 200 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="sent">Enviados</option>
            <option value="completed">Completados</option>
            <option value="canceled">Cancelados</option>
            <option value="failed">Fallidos</option>
          </select>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><Bell size={64} /><h3>Sin recordatorios</h3></div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filtered} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filtered.map((r) => (
              <div className="item-card" key={r.id}>
                <div className="item-card-header">
                  <div className="item-card-avatar">⏰</div>
                  <div style={{ flex: 1 }}>
                    <div className="item-card-title">{r.title}</div>
                    <div className="item-card-subtitle">{formatDate(r.remindAt)}</div>
                  </div>
                  {!fl.isHidden('status') && (
                    <span className={`badge ${STATUS_BADGE[r.status] || 'badge-gray'}`}>{r.status}</span>
                  )}
                </div>
                <div className="item-card-body">
                  {!fl.isHidden('targetName') && r.targetName && <div className="item-card-row" style={fl.cardStyle('targetName')}><span className="item-label">Destinatario</span><span>{r.targetName}</span></div>}
                  {!fl.isHidden('targetPhone') && r.targetPhone && <div className="item-card-row" style={fl.cardStyle('targetPhone')}><span className="item-label">Tel</span><span>{r.targetPhone}</span></div>}
                  {!fl.isHidden('notifyMethod') && <div className="item-card-row" style={fl.cardStyle('notifyMethod')}><span className="item-label">Método</span><span>{r.notifyMethod}</span></div>}
                  {!fl.isHidden('attempts') && <div className="item-card-row" style={fl.cardStyle('attempts')}><span className="item-label">Intentos</span><span>{r.attempts}/{r.maxAttempts}</span></div>}
                  {!fl.isHidden('description') && r.description && (
                    <div className="item-card-row" style={{ ...fl.cardStyle('description'), flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                      <span className="item-label">Detalle</span>
                      <span style={{ fontSize: 12, color: '#475569' }}>{r.description}</span>
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    <button className="btn-icon" onClick={() => remove(r.id)} title="Borrar" style={{ color: '#ef4444' }}><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reminders;
